import { Suspense, useEffect, useState } from "react";
import { apiService } from "../../api/apiAdapter";
import { isPWAInstalled } from "../../utils/isPWAInstalled";
import useScreenSize from "../../hooks/useScreenSize";

const TG_IDS = ["151", "168", "170"];

const getPrimarySubscription = (subscriptions) => {
  const allSubscriptions = Array.isArray(subscriptions)
    ? subscriptions
    : subscriptions
      ? [subscriptions]
      : [];

  return allSubscriptions.find((sub) => TG_IDS.includes(String(sub?.tg_subscriptionid))) || null;
};

export const formatExpiryDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function canAddOn({ tg_subscriptionid, tg_numberoflicences }, { isAddOnActive, isWebScreen }) {
  return (
    tg_subscriptionid === "168" &&
    tg_numberoflicences < 5 &&
    isAddOnActive &&
    !isPWAInstalled() &&
    isWebScreen
  );
}

export function canRenew({ isChild, isWebScreen, tg_subscriptionid }) {
  if (tg_subscriptionid === "168") {
    // multiUser no renewal
    return false;
  }
  return !isChild && !isPWAInstalled() && isWebScreen;
}
export function generateToken(length = 32) {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    token += characters[randomIndex];
  }
  return token;
}

const MySubscriptions = (props) => {
  const { subscriptions, contactId, isChild, push } = props;
  const { isWebScreen } = useScreenSize();
  const [selectedUsers, setSelectedUsers] = useState("");
  const [isAddOnActive, setIsAddOnActive] = useState(false);
  const currentSubscription = getPrimarySubscription(subscriptions);
  const showAddOn = currentSubscription
    ? canAddOn(currentSubscription, { isAddOnActive, isWebScreen })
    : false;
  const showRenewButton = canRenew({
    isChild,
    isWebScreen,
    tg_subscriptionid: currentSubscription?.tg_subscriptionid,
  });

  useEffect(() => {
    const checkSubscriptionStatus = () => {
      if (!currentSubscription?.tg_expirydate) return false;
      const endDate = new Date(currentSubscription.tg_expirydate);
      const today = new Date();
      return endDate > today;
    };
    setIsAddOnActive(checkSubscriptionStatus());
  }, [currentSubscription]);

  const handleRenewalClick = async () => {
    if (!currentSubscription) return;

    let token = localStorage.getItem("tgCartToken");
    if (!token) {
      token = generateToken();
      localStorage.setItem("tgCartToken", token);
    }

    const renewalItem = {
      token,
      subscriptionid: currentSubscription.tg_subscriptionid.toString(),
      quantity: currentSubscription.tg_numberoflicences || 1,
      ordertype: "Renew",
      currentSubscriptionId: currentSubscription.entitlementid,
      enddate: currentSubscription.tg_expirydate,
      hasappliedcoupon: currentSubscription.tg_couponreferrercode !== null,
    };

    try {
      const response = await apiService.addToCart(renewalItem);
      if (response.success) {
        setTimeout(() => push("/myCart"), 2000);
      } else {
        throw new Error(response.message || "Failed to add renewal to cart");
      }
    } catch (error) {
      console.error("Failed to add renewal to cart:", error);
      alert(error.message || "An error occurred while adding the renewal to cart.");
    }
  };

  const handleAddLicense = async () => {
    if (!currentSubscription) return;

    if (!selectedUsers) {
      alert("Please select the number of users to add");
      return;
    }

    let token = localStorage.getItem("tgCartToken");
    if (!token) {
      token = generateToken();
      localStorage.setItem("tgCartToken", token);
    }

    const addLicenseItem = {
      token,
      subscriptionid: currentSubscription.tg_subscriptionid.toString(),
      quantity: parseInt(selectedUsers),
      ordertype: "Addon",
      enddate: currentSubscription.tg_expirydate,
      startdate: currentSubscription.startdate,
      currentSubscriptionId: currentSubscription.entitlementid,
      currentmultilicensenumber: currentSubscription.tg_numberoflicences,
      hasappliedcoupon: currentSubscription.tg_couponreferrercode !== null,
      contactid: contactId,
    };

    try {
      const response = await apiService.addToCart(addLicenseItem);
      if (response.success) {
        setTimeout(() => push("/myCart"), 2000);
      } else {
        throw new Error(response.message || "Failed to add licenses to cart");
      }
    } catch (error) {
      console.error("Failed to add licenses to cart:", error);
      alert(error.message || "An error occurred while adding the licenses to cart.");
    }
  };

  const getAvailableLicenseOptions = () => {
    if (!currentSubscription || currentSubscription.tg_subscriptionid !== "168") {
      return [];
    }

    const maxLicenses = 5;
    const currentLicenses = currentSubscription.tg_numberoflicences;
    const availableLicenses = maxLicenses - currentLicenses;
    return Array.from({ length: availableLicenses }, (_, i) => i + 1);
  };

  const isRenewalPeriod = () => {
    if (!currentSubscription?.tg_expirydate) return false;
    if (currentSubscription.tg_numberoflicences > 5) return false;
    const endDate = new Date(currentSubscription.tg_expirydate);
    const today = new Date();

    // Calculate dates 4 months before and after expiry
    const fourMonthsBefore = new Date(endDate);
    fourMonthsBefore.setMonth(endDate.getMonth() - 3);

    const threeMonthsAfter = new Date(endDate);
    threeMonthsAfter.setMonth(endDate.getMonth() + 1);
    return today >= fourMonthsBefore && today <= threeMonthsAfter;
  };

  return (
    <>
      <Suspense fallback={<div className="loader"></div>}>
        {currentSubscription && (
          <div className="container-fluid">
            <div className="row">
              <div className="col-xs-12 col-lg-12 noLeftPadding noRightPadding">
                <h3 className="tab-section-heading">Therapeutic Guidelines</h3>
              </div>
            </div>

            <div className="row subscriptionBox">
              <div className="col-lg-7 col-md-6">
                <p>
                  <strong>Therapeutic Guidelines</strong> &nbsp;expiry&nbsp;
                  <strong>
                    <span id="dat0">{formatExpiryDate(currentSubscription.tg_expirydate)}</span>
                  </strong>
                </p>
              </div>
              <div className="text-right col-lg-5 col-md-6">
                <a href="/" className="btn btn-info btn-sm">
                  Therapeutic Guidelines online
                </a>
                {showRenewButton && (
                  <button
                    type="button"
                    className="btn btn-info btn-sm"
                    disabled={!isRenewalPeriod()}
                    onClick={handleRenewalClick}
                  >
                    Renew
                  </button>
                )}

                {!isChild && (
                  <div
                    className="info-box"
                    style={{
                      margin: "1rem 0 0",
                      fontWeight: "500",
                      lineHeight: "1.5",
                      textAlign: "left",
                    }}
                  >
                    If you want to purchase/renew an account with more than 1 licence, please
                    contact our Sales Team at sales@tg.org.au or call us on 1800 061 260
                  </div>
                )}
              </div>
            </div>
            {showAddOn && (
              <>
                <div className="row mTop20px">
                  <div className="col-xs-12">
                    <h4 className="tab-section-heading">Purchase additional licence</h4>
                  </div>
                </div>

                <div className="row form-horizontal">
                  <div className="col-md-6 col-sm-12">
                    <div className="form-group">
                      <label className="col-sm-8 col-xs-6 control-label">
                        Number of users to add:
                      </label>
                      <div className="col-sm-4 col-xs-6">
                        <select
                          id="usersToAdd"
                          name="usersToAdd"
                          className="form-control reducedPadding"
                          onChange={(e) => setSelectedUsers(e.target.value)}
                        >
                          <option value="">please select</option>
                          {getAvailableLicenseOptions().map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-12" style={{ textAlign: "left" }}>
                    <h5>
                      Additional licences can be purchased at any time. Licence price will be
                      calculated on a pro-rata basis with a single expiry date for all subscriptions
                      on an account.
                    </h5>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-12 text-right mTop12px">
                    <input
                      type="button"
                      value="Add licence"
                      className="btn btn-info btn-sm borderNone"
                      onClick={handleAddLicense}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Suspense>
    </>
  );
};

export default MySubscriptions;
